import { Image } from '@lumapps/lumx-images/types';
import { Wrex } from '@lumapps/wrex/types';

export enum ACTIVITY_PLAYERS {
    NATIVE = 'native',
    FLUTTER = 'flutter',
    SCORM = 'scorm',
    LEGACY = 'legacy',
}
/**
 * List of available views
 */
export enum TRAINING_COURSE_VIEWS {
    ACTIVITIES = 'activities',
    TOOLBOXES = 'toolboxes',
    LEADERBOARD = 'leaderboard',
    ACHIEVEMENTS = 'achievements',
}

export enum MODULE_STATUSES {
    TODO = 'todo',
    STARTED = 'started',
    COMPLETED = 'completed',
    SUCCEEDED = 'succeeded',
    FAILED = 'failed',
    LOCKED = 'locked',
}

export enum ACTIVITY_STATUSES {
    TODO = 'todo',
    STARTED = 'started',
    COMPLETED = 'completed',
    SUCCEEDED = 'succeeded',
    FAILED = 'failed',
    LOCKED = 'locked',
}

export enum LEARNING_OBJECT_STATUSES {
    TODO = 'todo',
    STARTED = 'started',
    COMPLETED = 'completed',
    SUCCEEDED = 'succeeded',
    FAILED = 'failed',
    LOCKED = 'locked',
}

/* List all different type of activities */
export enum ACTIVITY_TYPES {
    MATCH = 'match',
    COURSE = 'course',
    EVALUATION = 'evaluation',
    QUIZ = 'quiz',
    SEQUENCE = 'sequence',
    EXTERNAL = 'external',
}

export enum ACTIVITY_CONCLUSION_TYPES {
    NORMAL = 'normal',
    SCORED = 'scored',
    OUTCOME = 'outcome',
    DUEL = 'duel',
}

export enum ACTIVITY_CONTENT_STATUS {
    INITIAL = 'initial',
    LOADING = 'loading',
    ERROR = 'error',
    READY = 'ready',
}

/* List all different type of rendering for the 'course' activities */
export enum COURSE_RENDER_TYPES {
    MOBILE_COURSE = 'mobile_course',
    FLASH_GAME = 'flash_game',
    SURVEY = 'survey',
    TOOLBOX = 'toolbox',
}

/* List all different type of rendering for the 'quiz' activities */
export enum QUIZ_RENDER_TYPES {
    CHALLENGE = 'challenge',
    FILL_THE_GAP = 'fill_the_gap',
    GUESS_WHAT = 'guess_what',
    MULTIPLE_CHOICE_QUIZ = 'multi_quiz',
    QUIZ_GAME = 'quiz_game',
    TRAINING_GAME = 'training_game',
    WORDS_PICKER = 'words_picker',
}

/* List all different type of rendering for the 'match' activities */
export enum MATCH_RENDER_TYPES {
    SUSHI_GAME = 'sushi_game',
    WORDS_POOL = 'words_pool',
}

/* List all different type of rendering for the 'evaluation' activities */
export enum EVALUATION_RENDER_TYPES {
    PROFILING = 'profiling',
    RECOMMENDATION_QUIZ = 'recommendation_quiz',
}

/* List all different type of rendering for the 'sequence' activities */
export enum SEQUENCE_RENDER_TYPES {
    SCRAMBLE_GAME = 'scramble_game',
}

/* List all different type of rendering for the 'external' activities */
export enum EXTERNAL_RENDER_TYPES {
    LINK = 'link',
    SCORM = 'scorm',
    WEB = 'web',
}

export interface LearningString {
    value: string;
    lang?: string;
}

export interface LearningRichText {
    value: Wrex.Nodes;
    lang?: string;
}

export interface ActivityIntroduction {
    title: LearningRichText;
    description: LearningRichText;
    image: Image;
}

export interface ConclusionOutcome {
    text: LearningRichText;
    formScore: number;
    toScore: number;
}

export interface ActivityConclusion {
    type: ACTIVITY_CONCLUSION_TYPES;
    title: LearningRichText;
    description?: LearningRichText;
    success?: LearningRichText;
    failure?: LearningRichText;
    perfect?: LearningRichText;
    won?: LearningRichText;
    lost?: LearningRichText;
    done?: LearningRichText;
    outcomes?: ConclusionOutcome[];
}

export interface ActivityConfig {
    renderType: string;
    images: Image[];
}

export enum QUIZ_ANSWER_STATE {
    CORRECT_RESULTS,
    INCORRECT_RESULTS,
    DEFAULT,
    NEUTRAL,
    NEUTRAL_SELECTED,
}

export interface Activity {
    id: string;
    title: LearningString;
    type: ACTIVITY_TYPES;
    introduction?: ActivityIntroduction;
    conclusion?: ActivityConclusion;
    config?: ActivityConfig;
    status?: ACTIVITY_STATUSES;
    progress?: number;
    trainingId?: string;
    siteId?: string;
    player?: ACTIVITY_PLAYERS;
    scored?: boolean;
}

export interface Module {
    id: string;
    title: LearningString;
    status: MODULE_STATUSES;
    progress: number;
    cover: Image;
    activities: Activity[];
}

export interface ToolboxSection {
    id: string;
    title: LearningString;
    description: LearningRichText;
    cover: Image;
    activities: Activity[];
}

export interface Toolbox {
    id: string;
    title: LearningString;
    description: LearningRichText;
    cover: Image;
    sections: ToolboxSection[];
}

export interface QuizAnswer {
    id: string;
    answer: LearningRichText;
    isCorrect: boolean;
    scoreValue?: number;
    feedback?: LearningRichText;
}

export interface QuizQuestion {
    id: string;
    question: LearningRichText;
    hasMultipleAnswers?: boolean;
    answers: QuizAnswer[];
    correction?: LearningRichText;
    isOptional?: boolean;
}

export interface QuizActivity extends Activity {
    questions: QuizQuestion[];
}

export enum COURSE_CARD_TYPES {
    TUTORIAL = 'tutorial',
    INFO = 'info',
    FLIP = 'flip',
    TRANSITION = 'transition',
    QUIZ = 'quiz',
    SURVEY = 'survey',
    SLIDER_QUIZ = 'slider_quiz',
    INTRODUCTION = 'introduction',
    CONCLUSION = 'conclusion',
}

export interface CourseCard {
    id: string;
    type: COURSE_CARD_TYPES;
    title?: LearningRichText;
    recto?: LearningRichText;
    verso?: LearningRichText;
    quiz?: QuizQuestion;
    question?: LearningRichText;
    isOptional?: boolean;
    rectoFlipButton?: LearningString;
    versoFlipButton?: LearningString;
}

export interface CourseActivity extends Activity {
    cards: CourseCard[];
}

export const enum TRAINING_TYPE {
    TRAINING = 'training_course',
    LEARNING_PATH = 'learning_path',
    RECOMMENDATION_QUIZ = 'recommendation_quiz',
    SCORM_TRAINING = 'scorm_training',
}

export interface Category {
    id: string;
    title: LearningString;
    cover: Image;
}

export interface Language {
    code: string;
    name: string;
    native: string;
    regional: string;
    isRtl: boolean;
}

export interface Training {
    id: string;
    type: TRAINING_TYPE;
    title: LearningString;
    description?: LearningRichText;
    cover?: Image;
    categories?: Category[];
    languages?: Language[];
    isSandbox?: boolean;
    canComment?: boolean;
    hasAchievements?: boolean;
    hasLeaderboard?: boolean;
    toolboxes?: Toolbox[];
    status?: LEARNING_OBJECT_STATUSES;
    progress?: number;
    score?: number;
    success?: boolean;
    time?: number;
    totalTime?: number;
    points?: number;
    totalPoints?: number;
    sessionsCount?: number;
    isRecommended?: boolean;
    dueAt?: Date;
    accessEndAt?: Date;
}

export interface TrainingCourse extends Training {
    type: TRAINING_TYPE.TRAINING;
    modules: Module[];
}

export interface LearningPath extends Training {
    type: TRAINING_TYPE.LEARNING_PATH;
    items: Training[];
}

export enum SCORM_VERSIONS {
    SCORM_1_2 = '1.2',
    SCORM_2004 = '2004',
}

export interface ScormTraining extends Training {
    type: TRAINING_TYPE.SCORM_TRAINING;
    resourceHref: LearningString;
    scormVersion: LearningString;
    scormPlayerUrl: string;
    suspendData?: string;
    launchData?: string;
    location?: string;
}

export interface RecommendationQuiz extends Training {
    type: TRAINING_TYPE.RECOMMENDATION_QUIZ;
}

export interface EvaluationAnswerTheme {
    themeId: string;
    scoreValue: number;
}

export interface EvaluationAnswer {
    id: string;
    answer: LearningRichText;
    themes: EvaluationAnswerTheme[];
}

export interface EvaluationQuestion {
    id: string;
    question: LearningRichText;
    answers: EvaluationAnswer[];
    correction?: LearningRichText;
}

export enum EVALUATION_THEME_OUTCOME_TYPES {
    TRAINING = 'training',
    MODULE = 'module',
}

export interface MatchSet {
    id: string;
    name: LearningString;
    image: Image;
}

export interface MatchItem {
    id: string;
    setId: string;
    item: LearningRichText;
    scoreValue: number;
}

export interface MatchActivity extends Activity {
    sets: MatchSet[];
    items: MatchItem[];
}

export enum SEQUENCE_QUESTION_TYPES {
    IMAGE = 'image',
    SENTENCE = 'sentence',
    WORD = 'word',
}

export interface SequenceAnswer {
    id: string;
    answer: LearningRichText;
    isCorrect: boolean;
    scoreValue?: number;
}

export interface SequenceQuestion {
    id: string;
    type: SEQUENCE_QUESTION_TYPES;
    question: LearningRichText;
    correction?: LearningRichText;
    correct: Image[] | string[];
    trap: Image[] | string[];
}

export interface SequenceActivity extends Activity {
    questions: SequenceQuestion[];
}

export enum SESSION_LEGACY_ANSWER_TYPES {
    OPEN_TEXT = 'openText',
    SLIDER = 'slider',
}

export interface SessionLegacyAnswer {
    question: string;
    answer?: string;
    elapsedTime?: number;
    right?: boolean;
    type?: SESSION_LEGACY_ANSWER_TYPES;
    value?: string | number;
}

export interface SessionLegacyData {
    created?: Date;
    progress?: number;
    time?: number;
    answers?: SessionLegacyAnswer[];
    score?: number;
    success?: boolean;
    perfect?: boolean;
    points?: number;
}

export interface SessionLegacy {
    siteId?: string;
    trainingId?: string;
    activityId?: string;
    learnerId?: string;
    data: SessionLegacyData;
}

/**
 * Activity-level session event types.
 */
export enum ACTIVITY_ACTIONS {
    START = 'activity_start',
    DIFFICULTY_SELECTED = 'activity_difficulty_selected',
    OPPONENT_SELECTED = 'activity_opponent_selected',
    QUIZ_QUESTION_ANSWERED = 'activity_quiz_question_answered',
    MATCH_QUESTION_ANSWERED = 'activity_match_question_answered',
    EVALUATION_QUESTION_ANSWERED = 'activity_evaluation_question_answered',
    COURSE_SURVEY_QUESTION_ANSWERED = 'activity_course_survey_question_answered',
    COURSE_QUIZ_QUESTION_ANSWERED = 'activity_course_quiz_question_answered',
    COURSE_SLIDER_QUIZ_QUESTION_ANSWERED = 'activity_course_slider_quiz_question_answered',
    PAUSE = 'activity_pause',
    RESUME = 'activity_resume',
    PROGRESS_CHANGE = 'activity_progress_change',

    END = 'activity_end',
    CLOSE = 'activity_close',
    RETAKE = 'activity_retake',
    RELOAD = 'activity_reload',
}

/**
 * Session meta-operation types (internal slice operations — not real session event types).
 */
export enum SESSION_ACTIONS {
    ADD = 'add',
    RESET = 'session_reset',
    SEND = 'session_send',
    PUSH_FAILED = 'push_failed',
}

/**
 * Learning object event types.
 */
export enum LEARNING_OBJECT_ACTIONS {
    OPENED = 'learning_object_opened',
    TOOLBOX_DISPLAYED = 'learning_object_toolbox_displayed',
    SCORM_SESSION = 'learning_object_scorm_session',
    CLOSED = 'learning_object_closed',
}

export interface ActionSessionAdd {
    type: SESSION_ACTIONS.ADD;
}

export interface ActionSessionReset {
    type: SESSION_ACTIONS.RESET;
}

export interface ActionSessionSend {
    type: SESSION_ACTIONS.SEND;
    callback?: () => void;
}

export interface ActionSessionPushFailed {
    type: SESSION_ACTIONS.PUSH_FAILED;
}

export interface SessionActionAdd {
    type: SESSION_ACTIONS.ADD;
}

export interface SessionActionReset {
    type: SESSION_ACTIONS.RESET;
}

export interface SessionActionSend {
    type: SESSION_ACTIONS.SEND;
    callback?: () => void;
}
export interface SessionActionPushFailed {
    type: SESSION_ACTIONS.PUSH_FAILED;
}

/**
 * Action when an activity starts.
 */
export interface ActionActivityStart {
    type: ACTIVITY_ACTIONS.START;
    siteId?: string;
    learnerId?: string;
    trainingId?: string;
    activityId?: string;
    activityType?: ACTIVITY_TYPES;
}

/**
 * Action when a difficulty is selected.
 */
export interface ActionActivityDifficultySelected {
    type: ACTIVITY_ACTIONS.DIFFICULTY_SELECTED;
    difficultyId: string;
}

/**
 * Action when an opponent is selected.
 */
export interface ActionActivityOpponentSelected {
    type: ACTIVITY_ACTIONS.OPPONENT_SELECTED;
    opponentId: string;
}

/**
 * Action when a quiz question is answered.
 */
export interface ActionActivityQuizQuestionAnswered {
    type: ACTIVITY_ACTIONS.QUIZ_QUESTION_ANSWERED;
    questionId: string;
    answerIds: string[];
    elapsedTime: number;
}

/**
 * Action when a course quiz question is answered.
 */
export interface ActionActivityCourseQuizQuestionAnswered {
    type: ACTIVITY_ACTIONS.COURSE_QUIZ_QUESTION_ANSWERED;
    cardId: string;
    questionId: string;
    answerIds: string[];
    answersCorrectness: boolean[];
}

/**
 * Action when a survey question is answered.
 */
export interface ActionActivityCourseSurveyQuestionAnswered {
    type: ACTIVITY_ACTIONS.COURSE_SURVEY_QUESTION_ANSWERED;
    cardId: string;
    answer: string;
}

/**
 * Action when a slider quiz question is answered.
 */
export interface ActionActivityCourseSliderQuizQuestionAnswered {
    type: ACTIVITY_ACTIONS.COURSE_SLIDER_QUIZ_QUESTION_ANSWERED;
    cardId: string;
    questionId: string;
    answerIds: string[];
    values: number[];
}

/**
 * Action when an activity is paused.
 */
export interface ActionActivityPause {
    type: ACTIVITY_ACTIONS.PAUSE;
}

/**
 * Action when an activity is resumed.
 */
export interface ActionActivityResume {
    type: ACTIVITY_ACTIONS.RESUME;
}

export interface ActionActivityEndLegacyData {
    created?: Date;
    time?: number;
    progress?: number;
    points?: number;
    answers?: {
        question: string;
        right: boolean;
        answer: string;
    }[];
    score?: number;
    success?: boolean;
    perfect?: boolean;
}

/**
 * Action when an activity ends.
 */
export interface ActionActivityEnd {
    type: ACTIVITY_ACTIONS.END;
    legacyData?: SessionLegacy;
}

/**
 * Action reporting the current progress percentage.
 */
export interface ActionActivityProgressChange {
    type: ACTIVITY_ACTIONS.PROGRESS_CHANGE;
    progress: number;
}

/**
 * Action when the learner closes the activity.
 */
export interface ActionActivityClose {
    type: ACTIVITY_ACTIONS.CLOSE;
}

/**
 * Action when the learner retries a failed activity load.
 */
export interface ActionActivityRetake {
    type: ACTIVITY_ACTIONS.RETAKE;
}

/**
 * Action when the learner reload an activity.
 */
export interface ActionActivityReload {
    type: ACTIVITY_ACTIONS.RELOAD;
}

export interface ActionLearningObjectScormSessionLegacyData {
    created?: Date;
    time?: number;
    progress?: number;
    points?: number;
    score?: number;
    success?: boolean;
    perfect?: boolean;
    previousResult?: {
        location?: string;
        suspendData?: string;
    };
}

/**
 * Action when a scorm training sends a session.
 */
export interface ActionLearningObjectScormSession {
    type: LEARNING_OBJECT_ACTIONS.SCORM_SESSION;
    legacyData: {
        siteId: string;
        trainingId: string;
        learnerId: string;
        data: ActionLearningObjectScormSessionLegacyData;
    };
}

/**
 * Action when a learning object is opened.
 */
export interface ActionLearningObjectOpened {
    type: LEARNING_OBJECT_ACTIONS.OPENED;
    siteId: string;
    learnerId: string;
    trainingId: string;
    trainingType: TRAINING_TYPE;
}

/**
 * Action when a toolbox is displayed.
 */
export interface ActionLearningObjectToolboxDisplayed {
    type: LEARNING_OBJECT_ACTIONS.TOOLBOX_DISPLAYED;
    toolboxId: string;
}

/**
 * Action when a learning object is closed.
 */
export interface ActionLearningObjectClosed {
    type: LEARNING_OBJECT_ACTIONS.CLOSED;
}

/**
 * All possible actions.
 */
export type Action =
    | ActionSessionAdd
    | ActionSessionReset
    | ActionSessionPushFailed
    | ActionSessionSend
    | ActionActivityStart
    | ActionActivityDifficultySelected
    | ActionActivityOpponentSelected
    | ActionActivityQuizQuestionAnswered
    | ActionActivityCourseSurveyQuestionAnswered
    | ActionActivityCourseQuizQuestionAnswered
    | ActionActivityCourseSliderQuizQuestionAnswered
    | ActionActivityPause
    | ActionActivityResume
    | ActionActivityEnd
    | ActionActivityProgressChange
    | ActionActivityClose
    | ActionActivityRetake
    | ActionActivityReload
    | ActionLearningObjectScormSession
    | ActionLearningObjectOpened
    | ActionLearningObjectToolboxDisplayed
    | ActionLearningObjectClosed;

/**
 * Session event.
 */
export interface SessionEvent {
    action: Action;
    sessionId: string;
    createdAt: Date;
}

/**
 * Session.
 */
export interface Session {
    sessionId?: string;
    siteId?: string;
    learnerId?: string;
    trainingId?: string;
    trainingType?: TRAINING_TYPE;
    activityId?: string;
    activityType?: ACTIVITY_TYPES;
    createdAt?: Date;
    events: SessionEvent[];
}

export const enum TRAINING_LEGACY_MESSAGE_TYPE {
    NAVIGATION = 'askNavigation',
    BACK = 'askBack',
    OPEN_URL = 'askOpenUrl',
    OPEN_DOCUMENT = 'askOpenDocument',
    SCORM_PLAYER_READY = 'scorm_player_ready',
    SCORM_PLAYER_COMMIT = 'scorm_player_commit',
    SCORM_PLAYER_TERMINATE = 'scorm_player_terminate',
}

export const enum TRAINING_LEGACY_MESSAGE_NAVIGATION_TYPE {
    EVENT = 'event',
}

export const enum TRAINING_LEGACY_DATA_TYPE {
    TRAINING = 'Training',
    LEARNING_PATH = 'LearningPath',
    RECOMMENDATION_QUIZ = 'RecommendationQuiz',
}

export interface TrainingLegacy {
    accessEndsAt?: Date;
    accessUntil?: Date;
    availableLanguages: string[];
    categoriesCodes: string[];
    categoriesTitles: string[];
    code: string;
    cover: string;
    description: string;
    dueAt?: Date;
    dueDate?: Date | null;
    identifier: string;
    isDemo: boolean;
    isLive: boolean;
    isRecommended?: boolean;
    isSandbox: boolean;
    language: string;
    logo: string;
    nbCompletableActivities: number;
    nbCompletedActivities: number;
    progress: number;
    title: string;
    type: TRAINING_TYPE | TRAINING_LEGACY_DATA_TYPE;
}

export interface TrainingLegacyList {
    offset: number;
    limit: number;
    items: TrainingLegacy[];
}

export interface TrainingLegacyLaunchUrl {
    launchUrl: string;
    identifier: string;
    title: string;
    type: string;
    activityId?: string;
    duelId?: string;
}

export interface CategoryLegacy {
    title: string;
    image: string;
    code: string;
    parent?: string;
    children?: CategoryLegacy[];
    trainings?: TrainingLegacy[];
}

export interface CatalogLegacy {
    categories?: CategoryLegacy[];
    trainings?: TrainingLegacy[];
}

export interface CatalogSearch {
    query: string;
    results: TrainingLegacy[];
}

export interface FlatenedCategory {
    categoryData: CategoryLegacy;
    parentCode: string;
    depth: number;
}

export interface EvaluationThemeOutcome {
    id: string;
    type: EVALUATION_THEME_OUTCOME_TYPES;
    text: LearningRichText;
    fromScore: number;
    toScore: number;
    trainings?: TrainingLegacy[];
    modules?: Module[];
}

export interface EvaluationTheme {
    id: string;
    title: LearningRichText;
    maxScore: number;
    outcomes: EvaluationThemeOutcome[];
}

export interface EvaluationActivity extends Activity {
    questions: EvaluationQuestion[];
    themes: EvaluationTheme[];
    globalTheme: EvaluationTheme;
}

export interface ExternalActivity extends Activity {
    resourceHref: LearningString;
    scormVersion?: LearningString;
    scormPlayerUrl?: string;
}

export enum USER_ROLE_LEGACY {
    MANAGER = 'Manager',
    SUPER_ADMIN = 'SuperAdmin',
    ANALYST_MANAGER = 'AnalystManager',
    AUTHOR = 'Author',
    PERIMETER_TRAINER = 'PerimeterTrainer',
    COMMUNICATION_MANAGER = 'CommunicationManager',
    CONTENT_LICENCE_PROVIDER = 'ContentLicenseProvider',
    TRAINING_PUBLISHER = 'TrainingPublisher',
    PUBLISHER_MANAGER = 'PublisherManager',
    COMMENTS_MODERATOR = 'CommentsModerator',
    EXPERTISE_MANAGER = 'ExpertiseManager',
    AUTO_TRANSLATOR = 'AutoTranslator',
    ANALYTICS_FROM_APP = 'AnalyticsFromApp',
    COACH = 'Coach',
    ANALYST = 'Analyst',
    THEME_MANAGER = 'ThemeManager',
    TRAINER = 'Trainer',
    LEARNER_MANAGER = 'LearnerManager',
    TRAINING_MANAGER = 'TrainingManager',
    CONTENT_LICENSE_IMPORTER = 'ContentLicenseImporter',
    CATALOG_MANAGER = 'CatalogManager',
    ADMIN_MANAGER = 'AdminManager',
    LEARNERS_MASS_DELETER = 'LearnersMassDeleter',
    WALL_PUBLISHER_ADD_ONN = 'WallPublisherAddOn',
    EVENTS_MANAGER = 'EventsManager',
    AUTO_TRANSLATOR_COMM = 'AutoTranslatorComm',
}

export type UserRolesLegacy = USER_ROLE_LEGACY[];
